import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "../CSS/EmployeeCards.css";
import Sidebar from '../Component/Sidebar';
import { fetchEmployeesThunk } from "../features/employees/employeeThunks";
import { fetchTargetsThunk } from "../features/employeeTargets/employeeTargetThunks";
import { isAdmin, isHRRole } from "../RoleBasedRoute";

// ─── Skeleton ────────────────────────────────────────────────────────────────
const EmployeeCardSkeleton = () => (
  <div className="employee-card skeleton-card">
    <div className="card-top">
      <div className="employee-photo skeleton">
        <div className="skeleton-circle"></div>
      </div>
      <div className="card-actions">
        <div className="skeleton-dots"></div>
      </div>
    </div>
    <div className="employee-info">
      <div className="skeleton-text skeleton-title"></div>
      <div className="skeleton-text skeleton-subtitle"></div>
    </div>
    <div className="employee-content">
      <div className="employee-details-grid">
        <div className="detail-col">
          <div className="skeleton-text skeleton-label"></div>
          <div className="skeleton-text"></div>
        </div>
        <div className="detail-col">
          <div className="skeleton-text skeleton-label"></div>
          <div className="skeleton-text"></div>
        </div>
      </div>
      <div className="employee-contact">
        <div className="contact-item skeleton-text"></div>
        <div className="contact-item skeleton-text"></div>
      </div>
    </div>
  </div>
);

// ─── Department mapping ───────────────────────────────────────────────────────
const DEPARTMENT_NAMES = {
  'TECH_BACKEND': 'Tech Team (Backend)',
  'DEVELOPMENT': 'Development Team',
  'HR': 'HR',
  'OPERATIONS': 'Operations',
  'SALES_MARKETING': 'Sales/Marketing',
  'FLOOR_SUPPORT': 'Floor Support',
};

// ─── Helper: resolve employee fields from either flat or nested shape ─────────
const resolveEmpFields = (emp) => {
  // Name: prefer nested user.first_name/last_name, fall back to flat emp.name
  const nestedName = `${emp.user?.first_name || ''} ${emp.user?.last_name || ''}`.trim();
  const fullName   = nestedName || emp.name?.trim() || '';

  // Active status: emp.is_active is the canonical source of truth (see models.py save() override).
  // user.is_active is only a fallback for edge cases where the profile field is missing.
  const isActive = emp.is_active ?? emp.user?.is_active ?? true;

  // Email: prefer nested user.email, fall back to flat emp.email
  const email = emp.user?.email || emp.email || '';

  return { fullName, isActive, email };
};

// ─── Main component ───────────────────────────────────────────────────────────
const EmployeeCards = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { employees, loading: employeesLoading, error: employeesError } = useSelector(s => s.employees);
  const { targets, loading: targetsLoading, error: targetsError }       = useSelector(s => s.employeeTargets);
  const currentUser = useSelector(s => s.auth.user);

  // Who can see inactive employees
  const canSeeInactive = isAdmin(currentUser) || isHRRole(currentUser);
  // Who can add/create employees
  const canAddEmployee = isAdmin(currentUser) || isHRRole(currentUser);

  // ── Filters ────────────────────────────────────────────────────────────────
  const [statusFilter, setStatusFilter]         = useState("Active");
  const [searchTerm, setSearchTerm]             = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("All");

  // Re-fetch employees whenever the status filter changes so the API does the
  // heavy lifting — no stale inactive records leak through to the wrong tab.
  useEffect(() => {
    const params = {};
    if (statusFilter === 'Active')   params.is_active = 'true';
    if (statusFilter === 'Inactive') params.is_active = 'false';
    // statusFilter === 'All' sends no param → backend returns everything
    dispatch(fetchEmployeesThunk(params));
  }, [dispatch, statusFilter]);

  // Targets only need to load once — they are not status-filtered
  useEffect(() => {
    dispatch(fetchTargetsThunk());
  }, [dispatch]);

  // ── Derived values ─────────────────────────────────────────────────────────
  const availableDepartments = [...new Set(employees.map(e => e.department).filter(Boolean))];

  const filteredEmployees = employees.filter((emp) => {
    const { isActive, fullName, email } = resolveEmpFields(emp);

    // ── Status filter ──────────────────────────────────────────────────────
    if (!canSeeInactive) {
      if (!isActive) return false;
    } else {
      if (statusFilter === "Active"   && !isActive) return false;
      if (statusFilter === "Inactive" &&  isActive) return false;
    }

    // ── Department filter ──────────────────────────────────────────────────
    if (departmentFilter !== "All" && emp.department !== departmentFilter) return false;

    // ── Search filter ──────────────────────────────────────────────────────
    if (searchTerm.trim()) {
      const q         = searchTerm.toLowerCase();
      const nameLower = fullName.toLowerCase();
      const deptLabel = DEPARTMENT_NAMES[emp.department]?.toLowerCase() || '';

      const hit =
        nameLower.includes(q) ||
        (emp.role?.toLowerCase() || '').includes(q) ||
        (email.toLowerCase()).includes(q) ||
        (emp.department?.toLowerCase() || '').includes(q) ||
        deptLabel.includes(q) ||
        (emp.contact_no || '').includes(q);

      if (!hit) return false;
    }

    return true;
  });

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getStatusIndicator = (emp) => {
    const { isActive } = resolveEmpFields(emp);
    return isActive ? "status-active" : "status-inactive";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  };

  const handleMoreActions = (e, employeeId) => {
    e.stopPropagation();
    // TODO: replace with a dropdown/modal
    alert(`Actions for employee ID: ${employeeId}`);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setDepartmentFilter("All");
    setStatusFilter("Active");
  };

  const hasActiveFilters =
    searchTerm.trim() !== "" ||
    departmentFilter !== "All" ||
    (canSeeInactive && statusFilter !== "Active");

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className='index-box'>
      <Sidebar />
      <div className="employee-container">

        {/* ── Top header row ── */}
        <div className="employee-header-controls">
          <h2 className="employee-title">
            <span className="employee-count">{filteredEmployees.length}</span>
            &nbsp;{filteredEmployees.length === 1 ? "Employee" : "Employees"}
            {statusFilter !== "All" && (
              <span className="title-status-badge" data-status={statusFilter.toLowerCase()}>
                {statusFilter}
              </span>
            )}
          </h2>

          <div className="employee-actions-group">
            {canAddEmployee && (
              <button className="add-candidate-button" onClick={() => navigate('/employees/new')}>
                + Add Employee
              </button>
            )}
          </div>
        </div>

        {/* ── Filter bar ── */}
        <div className="filter-bar">
          {/* Search */}
          <div className="search-wrapper">
            <svg className="search-icon" viewBox="0 0 24 24" width="16" height="16">
              <path fill="currentColor"
                d="M9.5 3a6.5 6.5 0 0 1 5.03 10.617l4.427 4.426-1.414 1.414-4.426-4.427A6.5 6.5 0 1 1 9.5 3zm0 2a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9z"/>
            </svg>
            <input
              className="search-input"
              type="text"
              placeholder="Search by name, role, email, department…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="search-clear" onClick={() => setSearchTerm("")}>✕</button>
            )}
          </div>

          {/* Department dropdown */}
          <select
            className="filter-select"
            value={departmentFilter}
            onChange={e => setDepartmentFilter(e.target.value)}
          >
            <option value="All">All Departments</option>
            {availableDepartments.map(dept => (
              <option key={dept} value={dept}>
                {DEPARTMENT_NAMES[dept] || dept}
              </option>
            ))}
          </select>

          {/* Status dropdown — only for Admin / HR */}
          {canSeeInactive && (
            <select
              className="filter-select"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="All">All Statuses</option>
            </select>
          )}

          {/* Clear filters */}
          {hasActiveFilters && (
            <button className="clear-filters-btn" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>

        {/* ── Cards grid ── */}
        <div className="employee-cards">
          {(employeesLoading || targetsLoading) &&
            Array(6).fill(0).map((_, i) => <EmployeeCardSkeleton key={i} />)
          }

          {(employeesError || targetsError) && !employeesLoading && !targetsLoading && (
            <div className="error-container">
              Error loading data: {employeesError || targetsError}
            </div>
          )}

          {!employeesLoading && !targetsLoading && !employeesError && !targetsError && (
            filteredEmployees.length === 0
              ? (
                <div className="empty-state">
                  <svg viewBox="0 0 24 24" width="48" height="48">
                    <path fill="#ccc"
                      d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                  </svg>
                  <p>No employees match your filters.</p>
                  <button className="clear-filters-btn" onClick={clearFilters}>Clear filters</button>
                </div>
              )
              : filteredEmployees.map((emp) => {
                  const { fullName, isActive, email } = resolveEmpFields(emp);

                  return (
                    <div
                      className={`employee-card${!isActive ? ' card-inactive' : ''}`}
                      key={emp.id}
                      onClick={() => navigate(`/employees/${emp.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="card-top">
                        <div className="employee-photo">
                          <img
                            src={
                              emp.image ||
                              `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName || 'U')}&background=random&size=80`
                            }
                            alt={fullName}
                          />
                          <div className={`employee-status ${getStatusIndicator(emp)}`}></div>
                        </div>

                        <div className="card-actions">
                          {canAddEmployee && (
                            <button className="more-button" onClick={(e) => handleMoreActions(e, emp.id)}>
                              •••
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="employee-info">
                        <h3 className="employee-name">{fullName || '—'}</h3>
                        <p className="employee-role">{emp.role || '—'}</p>
                        {emp.department && (
                          <p className="employee-department">
                            🏢 {DEPARTMENT_NAMES[emp.department] || emp.department}
                          </p>
                        )}
                        {/* Inactive badge visible only to privileged users */}
                        {canSeeInactive && !isActive && (
                          <span className="inactive-badge">Inactive</span>
                        )}
                      </div>

                      <div className="employee-content">
                        <div className="employee-details-grid">
                          <div className="detail-col">
                            <div className="detail-label">Department</div>
                            <div className="detail-value">
                              {DEPARTMENT_NAMES[emp.department] || emp.department || "—"}
                            </div>
                          </div>
                          <div className="detail-col">
                            <div className="detail-label">Hired Date</div>
                            <div className="detail-value">{formatDate(emp.date_of_joining)}</div>
                          </div>
                        </div>

                        <div className="employee-contact">
                          <div className="contact-item">
                            <span className="contact-icon">✉</span>
                            {email || '—'}
                          </div>
                          <div className="contact-item">
                            <span className="contact-icon">☎</span>
                            {emp.contact_no || '—'}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeCards;